import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Loader2, ArrowUpRight, Clock } from "lucide-react";
import { BUSINESS, PROPERTY_TYPES, SERVICE_OPTIONS, IMAGES } from "../../data/content";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  first_name: "", last_name: "", phone: "", email: "",
  property_type: "", service_needed: "", message: "",
};

const inputCls =
  "w-full bg-transparent border-0 border-b border-fc-ink/20 py-3 text-fc-ink placeholder:text-fc-ink2/50 focus:outline-none focus:border-fc-cobalt transition-colors";

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["first_name", "last_name", "phone", "email", "property_type", "service_needed"];
    if (required.some((k) => !String(form[k] || "").trim())) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/service-requests`, form);
      toast.success("Request received. F&C Air Conditioning will follow up shortly.");
      setForm(EMPTY);
    } catch (err) {
      if (err?.response?.status === 422) {
        toast.error("Please check your details and try again.");
      } else {
        toast.error("Something went wrong. Please call us directly.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="surface-dark text-white py-24 lg:py-36">
      <div className="mx-auto w-[92%] max-w-[1280px] grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-fc-cyan">Request Service</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display font-semibold tracking-tighter leading-[0.95] text-4xl md:text-6xl">
              Let’s get your comfort back.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-white/60 max-w-md">
              Tell us what you need and the F&amp;C Air Conditioning team can follow up with you.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col gap-3 max-w-md">
              <a data-testid="contact-phone" href={BUSINESS.phoneHref} className="group flex items-center gap-4 border border-white/12 p-5 hover:border-fc-cyan transition-colors">
                <Phone size={20} className="text-fc-cyan" />
                <div>
                  <div className="font-display text-xl tracking-tight">{BUSINESS.phone}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Call F&amp;C Air Conditioning</div>
                </div>
                <ArrowUpRight size={18} className="ml-auto opacity-40 group-hover:opacity-100 transition-opacity" />
              </a>
              <a data-testid="contact-email" href={`mailto:${BUSINESS.email}`} className="group flex items-center gap-4 border border-white/12 p-5 hover:border-fc-cyan transition-colors">
                <Mail size={20} className="text-fc-cyan" />
                <div>
                  <div className="font-display text-xl tracking-tight break-all">{BUSINESS.email}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Email us</div>
                </div>
                <ArrowUpRight size={18} className="ml-auto opacity-40 group-hover:opacity-100 transition-opacity shrink-0" />
              </a>
              <div data-testid="contact-hours" className="border border-white/12 p-5">
                <div className="flex items-center gap-3">
                  <Clock size={20} className="text-fc-cyan" />
                  <div className="font-display text-xl tracking-tight">Business Hours</div>
                </div>
                <div className="mt-4 space-y-1.5 text-sm text-white/70">
                  <div className="flex justify-between"><span>Monday – Friday</span><span className="text-white">8:00 AM – 6:00 PM</span></div>
                  <div className="flex justify-between"><span>Saturday</span><span className="text-white">9:00 AM – 2:00 PM</span></div>
                  <div className="flex justify-between"><span>Sunday</span><span className="text-white">Closed</span></div>
                </div>
                <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-fc-orange">Emergency service available</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div data-testid="contact-van-photo" className="mt-8 max-w-md overflow-hidden border border-white/12">
              <img
                src={IMAGES.vanRear}
                alt="F&C Air Conditioning branded service van ready for dispatch"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover aspect-[4/3]"
              />
            </div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/50 max-w-md">
              Our fleet is on the road across South Florida
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form data-testid="service-form" onSubmit={submit} className="bg-white text-fc-ink p-8 lg:p-10">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="first_name" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">First Name *</label>
                <input id="first_name" data-testid="input-first-name" className={inputCls} value={form.first_name} onChange={set("first_name")} placeholder="Jane" />
              </div>
              <div>
                <label htmlFor="last_name" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">Last Name *</label>
                <input id="last_name" data-testid="input-last-name" className={inputCls} value={form.last_name} onChange={set("last_name")} placeholder="Doe" />
              </div>
              <div>
                <label htmlFor="phone" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">Phone *</label>
                <input id="phone" data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="954-000-0000" />
              </div>
              <div>
                <label htmlFor="email" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">Email *</label>
                <input id="email" data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </div>
              <div>
                <label htmlFor="property_type" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">Property Type *</label>
                <select id="property_type" data-testid="select-property-type" className={inputCls} value={form.property_type} onChange={set("property_type")}>
                  <option value="">Select…</option>
                  {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="service_needed" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">Service Needed *</label>
                <select id="service_needed" data-testid="select-service" className={inputCls} value={form.service_needed} onChange={set("service_needed")}>
                  <option value="">Select…</option>
                  {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-6">
              <label htmlFor="message" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">Message</label>
              <textarea id="message" data-testid="input-message" rows={3} className={inputCls + " resize-none"} value={form.message} onChange={set("message")} placeholder="Tell us what's going on…" />
            </div>
            <button
              data-testid="submit-service-request"
              type="submit"
              disabled={loading}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-fc-orange text-white py-4 font-bold uppercase tracking-wide text-sm hover:brightness-95 transition-all disabled:opacity-60"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Submit Service Request <ArrowUpRight size={18} /></>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
